import { isEmpty } from "lodash";

import {
  Answers,
  ConfirmationAnswer,
  ConfirmationAnswerWithDetails,
  Detail,
  MappedAnswers,
  PSROutcome,
  PSRServices,
  TemporaryNeedsQuestions,
} from "../context/vulnerability/VulnerabilityContext";

import { ageQuestions } from "./About/Age/AgeDetail";
import { childrenQuestions } from "./About/Children/Children";
import { chronicQuestions } from "./About/ChronicIllness/ChronicIllnessDetail";
import { financialQuestions } from "./About/Financial/FinancialDetail";
import { medicalQuestions } from "./About/MedicalEquipment/MedicalEquipmentDetail";
import { miscQuestions } from "./About/Miscellaneous/Miscellaneous";
import { sightQuestions } from "./About/Sight/SightDetail";
import { PSRServicesToTextSummary } from "./PSRServicesSelection/constants";

const allDetailQuestions: Record<string, string> = {
  ...ageQuestions,
  ...childrenQuestions,
  ...chronicQuestions,
  ...financialQuestions,
  ...medicalQuestions,
  ...miscQuestions,
  ...sightQuestions,
};

const allPsrAnswers = {
  children: "Families with young children 5 or under",
  hearing: "Hearing impairment (inc. Deaf)",
  partiallySighted: "Partially sighted",
  legallyBlind: "Blind",
  pensionableAge: "Pensionable age",
  nebuliserOrApnoeaMonitor: "Nebuliser and apnoea monitor",
  heartOrLungOrVentilatorEquipment: "Heart, lung & ventilator",
  dialysisOrFeedingPumpOrAutomatedMedication:
    "Dialysis, feeding pump and automated medication",
  oxygenConcentrator: "Oxygen concentrator",
  stairLiftOrHoistOrElectricBed: "Stair lift, hoist, electric bed",
  medicallyDependantShowering: "Medically dependant showering, bathing",
  hasCarelineInstalledOrReliantOnCommsServices: "Careline, telecare system",
  unableToCommunicateInEnglish: "Unable to communicate in English",
  dementiaOrCognitiveImpairment: "Dementia(s), Cognitive impairment",
  physicalImpairment: "Physical impairment",
  mentalHealthIssue: "Mental health",
  speechImpairment: "Speech impairment",
  recentlyRecoveringFromAHospitalVisit: "Temporary Post hospital recovery",
  restrictiveHandMovement: "Restricted hand movement",
  unableToAnswerTheDoor: "Unable to answer the door",
  additionalPresencePreferred: "Additional presence preferred",
  poorSenseOfSmellOrTaste: "Poor sense of smell, taste",
  temporaryYoungAdultHouseholderUnder18:
    "Temporary young adult householder ( <18 )",
  waterDependent: "Water dependent",
  medicineRefrigeration: "Medicine refrigeration",
  chronicIllness: "Chronic, serious illness",
  temporaryLifeChanges: "Temporary Life changes",
  learningDifficultiesOrDisablities: "Developmental condition",
};

export enum TemporaryNeedsSummaryTitle {
  YoungestChildAge = "Date of birth of your youngest child",
  Pregnant = "Due date",
  Bereavement = "Date of bereavement",
  LivingAlone = "Confirmed date of birth (18 years, living alone)",
  Hospital = "Date you left hospital",
}

export const filterAndMapAnswer = (
  detailValue: ConfirmationAnswerWithDetails | Record<string, boolean> | string,
) => {
  if (typeof detailValue === "string") {
    return detailValue;
  }

  const filtered = Object.entries(detailValue)
    .filter((item) => item[1] === true)
    .map((k) => {
      return [allDetailQuestions[k[0]]];
    });
  if (filtered.length > 0) {
    return Object.fromEntries(filtered);
  } else {
    return "No";
  }
};

const mapPSRServicesToText = (value: PSRServices): string => {
  let summary = "";
  Object.entries(value).forEach(([key, value]) => {
    if (value || value != "") {
      summary += PSRServicesToTextSummary[key as keyof PSRServices] + ", ";
    }
    return summary;
  });
  return summary.trim().slice(0, -1);
};

const mapPSROutcomeToText = (value: PSROutcome): string => {
  if (value.eligible && value.optedIn) {
    return "Yes";
  }
  return "No";
};

export const mapPSRAnswersSummary = (answers: Answers) => {
  const ans: MappedAnswers = {};
  if (answers.psr) {
    if (answers.psr.psrOutcome) {
      ans["psrOutcome"] = {
        title: "Added to the Priority Services Register (PSR)",
        summary: mapPSROutcomeToText(answers.psr.psrOutcome),
      };
    }

    if (answers.psr.psrTemporaryNeedsCodes) {
      if (answers.psr.psrTemporaryNeedsCodes.bereavementDate) {
        ans["bereavementDate"] = buildTemporaryNeedsSummary(
          answers.psr.psrTemporaryNeedsCodes.bereavementDate,
          TemporaryNeedsSummaryTitle.Bereavement,
        );
      }

      if (answers.psr.psrTemporaryNeedsCodes.youngestChildAgeDate) {
        ans["youngestChildAgeDate"] = answers.children?.pregnant
          ? buildTemporaryNeedsSummary(
              answers.psr.psrTemporaryNeedsCodes.youngestChildAgeDate,
              TemporaryNeedsSummaryTitle.Pregnant,
            )
          : buildTemporaryNeedsSummary(
              answers.psr.psrTemporaryNeedsCodes.youngestChildAgeDate,
              TemporaryNeedsSummaryTitle.YoungestChildAge,
            );
      }

      if (answers.psr.psrTemporaryNeedsCodes.livingAloneDate) {
        ans["livingAloneDate"] = buildTemporaryNeedsSummary(
          answers.psr.psrTemporaryNeedsCodes.livingAloneDate,
          TemporaryNeedsSummaryTitle.LivingAlone,
        );
      }

      if (answers.psr.psrTemporaryNeedsCodes.hospitalDate) {
        ans["hospitalDate"] = buildTemporaryNeedsSummary(
          answers.psr.psrTemporaryNeedsCodes.hospitalDate,
          TemporaryNeedsSummaryTitle.Hospital,
        );
      }
    }

    if (answers.psr.psrServices) {
      ans["psrServices"] = {
        title: "Services you require",
        summary: Object.values(answers.psr.psrServices).every((val) => !val)
          ? "No additional services required"
          : mapPSRServicesToText(answers.psr.psrServices),
      };
    }
  }

  return ans;
};

// This function merges questions that when answered as yes, result in a "Details" answer. Those lists of details will be added as the value of the primary question as a key-value pair.
// If answered 'No' or "Skip", we use this as the value for the key-value pair.
export const mapAnswerSummary = (answers: Answers) => {
  const ans: MappedAnswers = {};
  Object.entries(answers).forEach(([key, value]) => {
    // Does not include additonal comments or the 'additional detail' questions in the summary
    if (key.includes("Detail") || key === "psr") {
      return;
    }

    if (key === "ageCare" && answers.age !== ConfirmationAnswer.Yes) {
      return;
    }

    if (value === ConfirmationAnswer.Yes) {
      const detailsValue = answers[
        `${key}Detail` as keyof Answers
      ] as ConfirmationAnswerWithDetails;

      if (detailsValue) {
        const result = filterAndMapAnswer(detailsValue);
        ans[
          key as keyof Pick<
            MappedAnswers,
            | "sight"
            | "age"
            | "medicalEquipment"
            | "chronicIllness"
            | "financial"
            | "miscellaneous"
            | "dailyActivity"
            | "dailySupport"
          >
        ] = result;
        return;
      }
    }

    if (value instanceof Object) {
      value = filterAndMapAnswer(value);
    }

    ans[key as keyof MappedAnswers] = value;
  });

  return ans;
};

export const checkIfPsrAnswersAreEmpty = (answers: Answers): boolean => {
  const psrAnswers = buildPsrSummary(answers);

  return Object.values(psrAnswers)
    .map((value) => {
      return isEmpty(value);
    })
    .every((v) => !!v);
};

const buildChildrenPsrSummary = (detail: Detail): string | undefined => {
  if (detail.pregnant || detail.childrenUnder2 || detail.childrenBetween2And4) {
    return allPsrAnswers.children;
  }
  return;
};

const buildSightPsrSummary = (detail: Detail): string[] => {
  const summary: string[] = [];
  if (
    detail.mildVisualImpairment ||
    detail.moderateVisualImpairment ||
    detail.severeVisualImpairment
  ) {
    summary.push(allPsrAnswers.partiallySighted);
  }

  if (detail.legallyBlind) {
    summary.push(allPsrAnswers.legallyBlind);
  }

  return summary;
};

const buildAgePsrSummary = (detail: Detail): string | undefined => {
  if (detail.between66And74 || detail.between75And84 || detail.over85) {
    return allPsrAnswers.pensionableAge;
  }

  return;
};

const buildMedicalEquipmentPsrSummary = (detail: Detail): string[] => {
  const summary: string[] = [];

  if (detail.dialysisOrFeedingPumpOrAutomatedMedication) {
    summary.push(allPsrAnswers.dialysisOrFeedingPumpOrAutomatedMedication);
  }

  if (detail.heartOrLungOrVentilatorEquipment) {
    summary.push(allPsrAnswers.heartOrLungOrVentilatorEquipment);
  }

  if (detail.medicallyDependantShowering) {
    summary.push(allPsrAnswers.medicallyDependantShowering);
  }

  if (detail.nebuliserOrApnoeaMonitor) {
    summary.push(allPsrAnswers.nebuliserOrApnoeaMonitor);
  }

  if (detail.oxygenConcentrator) {
    summary.push(allPsrAnswers.oxygenConcentrator);
  }

  if (detail.stairLiftOrHoistOrElectricBed) {
    summary.push(allPsrAnswers.stairLiftOrHoistOrElectricBed);
  }

  return summary;
};

const buildMiscPsrSummary = (detail: Detail): Record<string, boolean> => {
  const summary: Record<string, boolean> = {};

  if (detail.hasCarelineInstalledOrReliantOnCommsServices) {
    summary[allPsrAnswers.hasCarelineInstalledOrReliantOnCommsServices] = true;
  }

  if (detail.unableToCommunicateInEnglish) {
    summary[allPsrAnswers.unableToCommunicateInEnglish] = true;
  }

  if (detail.dementiaOrCognitiveImpairment) {
    summary[allPsrAnswers.dementiaOrCognitiveImpairment] = true;
  }

  if (detail.physicalImpairment) {
    summary[allPsrAnswers.physicalImpairment] = true;
  }

  if (detail.mentalHealthIssue) {
    summary[allPsrAnswers.mentalHealthIssue] = true;
  }

  if (detail.speechImpairment) {
    summary[allPsrAnswers.speechImpairment] = true;
  }

  if (detail.recentlyRecoveringFromAHospitalVisit) {
    summary[allPsrAnswers.recentlyRecoveringFromAHospitalVisit] = true;
  }

  if (detail.restrictiveHandMovement) {
    summary[allPsrAnswers.restrictiveHandMovement] = true;
  }

  if (detail.unableToAnswerTheDoor) {
    summary[allPsrAnswers.unableToAnswerTheDoor] = true;
  }

  if (detail.additionalPresencePreferred) {
    summary[allPsrAnswers.additionalPresencePreferred] = true;
  }

  if (detail.poorSenseOfSmellOrTaste) {
    summary[allPsrAnswers.poorSenseOfSmellOrTaste] = true;
  }

  if (detail.temporaryYoungAdultHouseholderUnder18) {
    summary[allPsrAnswers.temporaryYoungAdultHouseholderUnder18] = true;
  }

  if (detail.waterDependent) {
    summary[allPsrAnswers.waterDependent] = true;
  }

  if (detail.medicineRefrigeration) {
    summary[allPsrAnswers.medicineRefrigeration] = true;
  }

  if (detail.neurologicalDiseases) {
    summary[allPsrAnswers.chronicIllness] = true;
  }

  if (detail.learningDifficultiesOrDisablities) {
    summary[allPsrAnswers.learningDifficultiesOrDisablities] = true;
  }

  return summary;
};

const buildChronicIllnessPsrSummary = (
  detail: Detail,
  medicalRefrigeration?: boolean,
): string | undefined => {
  if (
    detail.cancer ||
    detail.cardiovascularDisease ||
    detail.chronicRespiratoryDiseases ||
    detail.kidneyDisease ||
    detail.liverDisease ||
    detail.organFailure ||
    detail.copd ||
    detail.other
  ) {
    return allPsrAnswers.chronicIllness;
  }

  if (detail.diabetes && medicalRefrigeration) {
    return allPsrAnswers.chronicIllness;
  }

  return;
};

const buildFinancialPsrSummary = (detail: Detail): string | undefined => {
  if (detail.recentBereavement) {
    return allPsrAnswers.temporaryLifeChanges;
  }

  return;
};

export const buildPsrSummary = (answers: Answers): MappedAnswers => {
  const summary: MappedAnswers = {};

  if (answers.children) {
    summary.children = buildChildrenPsrSummary(answers.children);
  }

  if (answers.hearing === ConfirmationAnswer.Yes) {
    summary.hearing = allPsrAnswers.hearing;
  }

  if (answers.sight === ConfirmationAnswer.Yes && answers.sightDetail) {
    summary.sight = buildSightPsrSummary(answers.sightDetail);
  }

  if (answers.age === ConfirmationAnswer.Yes && answers.ageDetail) {
    summary.age = buildAgePsrSummary(answers.ageDetail);
  }

  if (
    answers.medicalEquipment === ConfirmationAnswer.Yes &&
    answers.medicalEquipmentDetail
  ) {
    summary.medicalEquipment = buildMedicalEquipmentPsrSummary(
      answers.medicalEquipmentDetail,
    );
  }

  if (answers.miscellaneous) {
    summary.miscellaneous = buildMiscPsrSummary(answers.miscellaneous);
  }

  if (
    answers.chronicIllness === ConfirmationAnswer.Yes &&
    answers.chronicIllnessDetail
  ) {
    summary.chronicIllness = buildChronicIllnessPsrSummary(
      answers.chronicIllnessDetail,
      answers.miscellaneous?.medicineRefrigeration,
    );
  }

  if (answers.financial === ConfirmationAnswer.Yes && answers.financialDetail) {
    summary.financial = buildFinancialPsrSummary(answers.financialDetail);
  }

  return summary;
};

export const checkIfPermanentNeeds = (answers: Answers): boolean => {
  const psrAnswers = buildPsrSummary(answers);

  return Object.values(psrAnswers)
    .map((value) => {
      if (isEmpty(value)) {
        return false;
      }

      if (value === allPsrAnswers.children) {
        return false;
      }

      if (
        value instanceof Object &&
        (!!value[allPsrAnswers.recentlyRecoveringFromAHospitalVisit] ||
          !!value[allPsrAnswers.temporaryYoungAdultHouseholderUnder18])
      ) {
        return false;
      }

      if (value === allPsrAnswers.temporaryLifeChanges) {
        return false;
      }

      return !isEmpty(value);
    })
    .some((v) => !!v);
};

export const checkIfTemporaryNeedsEmpty = (answers: Answers): boolean => {
  const temporaryNeeds = buildTemporaryNeedsQuestions(answers);

  return Object.values(temporaryNeeds)
    .map((value) => {
      return isEmpty(value);
    })
    .every((v) => !!v);
};

export const buildTemporaryNeedsSummary = (
  date: Date,
  temporaryNeed: TemporaryNeedsSummaryTitle,
): Record<string, string> => {
  return {
    title: temporaryNeed,
    summary: date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }),
  };
};

export const buildTemporaryNeedsQuestions = (
  answers: Answers,
): TemporaryNeedsQuestions => {
  const questions: TemporaryNeedsQuestions = {};

  if (answers.children) {
    if (
      answers.children.childrenUnder2 ||
      answers.children.childrenBetween2And4
    ) {
      questions.childUnder5 = {
        question: "What’s the birthday of your youngest child?",
        reason: `You're seeing this because you've told us you have a`,
      };
    }

    if (answers.children.pregnant) {
      questions.childUnder5 = {
        question: "When is your due date?",
        reason: `You're seeing this because you've told us you are`,
      };
    }
  }

  if (answers.miscellaneous) {
    if (answers.miscellaneous.recentlyRecoveringFromAHospitalVisit) {
      questions.beenInHospital = {
        question: "What date did you leave hospital?",
        reason: `You’re seeing this because you’ve told us you have recently`,
      };
    }

    if (answers.miscellaneous.temporaryYoungAdultHouseholderUnder18)
      questions.livingAloneUnder18 = {
        question: "Please confirm your date of birth.",
        reason: `You’re seeing this because you’ve told us you are`,
      };
  }

  if (answers.financial && answers.financialDetail) {
    if (answers.financialDetail.recentBereavement) {
      questions.bereavement = {
        question: "When did this challenging time begin?",
        reason: `You’re seeing this because you’ve told us you have recently experienced a`,
      };
    }
  }

  return questions;
};

export const mapConfirmationAnswer = (
  confirmationAns: ConfirmationAnswer | undefined,
): boolean => {
  return confirmationAns === "Yes" ? true : false;
};

export const noPsrServicesRequired = (
  services: PSRServices | undefined,
): boolean => {
  if (services) {
    return Object.values(services).every((v) => v === false);
  }
  return true;
};
